import type {
  ActionProposalArtifact,
  ActionProposalItemStatus,
  ActionProposalStatus,
  ActionReceiptSummary,
} from "~/shared/ai/assistant-action-artifact";

import { ActionProposalArtifactSchema } from "~/shared/ai/assistant-action-artifact";

export interface ActionSelectionPatch {
  includeItemKeys: string[];
  excludeItemKeys: string[];
  /** Complete displayed selection from the loaded review, including off-page costs. */
  selectedItemKeys?: string[];
}

export interface ActionExecutionState {
  completed: number;
  total: number;
  itemOutcomes: Record<string, { status: ActionProposalItemStatus; message: string; resultEntityId?: string }>;
}

export interface ActionArtifactState {
  artifacts: Record<string, ActionProposalArtifact>;
  families: Record<string, string>;
  stagedSelectionPatches: Record<string, ActionSelectionPatch>;
  execution: Record<string, ActionExecutionState>;
  /** A card press that failed, on that card's own line; it never reaches the composer or the thread. */
  controlErrors?: Record<string, string>;
}

/** Records or clears the failure line of a card press. */
export function setActionControlError(state: ActionArtifactState, proposalId: string, message: string | null): ActionArtifactState {
  const { [proposalId]: _cleared, ...rest } = state.controlErrors ?? {};
  return { ...state, controlErrors: message ? { ...rest, [proposalId]: message } : rest };
}

/** One live version: the newest artifact of the family a pane opened, or the opened one when it is the newest. */
export function latestFamilyArtifact(state: ActionArtifactState, proposalId: string | null): ActionProposalArtifact | null {
  const opened = proposalId ? state.artifacts[proposalId] : undefined;
  if (!opened) {
    return null;
  }
  const latestId = state.families[opened.familyId];
  return (latestId ? state.artifacts[latestId] : undefined) ?? opened;
}

export const emptyActionArtifactState = (): ActionArtifactState => ({
  artifacts: {},
  families: {},
  stagedSelectionPatches: {},
  execution: {},
});

export function applyActionProposalCommit(
  state: ActionArtifactState,
  rawArtifact: unknown,
  orgId: string,
): { state: ActionArtifactState; applied: boolean; proposalId?: string; rejectionReason?: string } {
  if (rawArtifact === undefined) {
    return { state, applied: false };
  }
  const parsed = ActionProposalArtifactSchema.safeParse(rawArtifact);
  if (!parsed.success || parsed.data.orgId !== orgId) {
    return { state, applied: false, rejectionReason: "invalid_artifact" };
  }
  // Whether an action is supported is decided on the server; the browser checks only the generic shape.
  const artifact = parsed.data;
  const currentProposalId = state.families[artifact.familyId];
  const current = currentProposalId ? state.artifacts[currentProposalId] : undefined;
  if (current && current.version > artifact.version) {
    return { state, applied: false, rejectionReason: "stale_version" };
  }
  const mergedArtifact =
    !artifact.binding && current?.proposalId === artifact.proposalId && current.binding
      ? { ...artifact, binding: current.binding }
      : artifact;
  const artifacts = { ...state.artifacts, [artifact.proposalId]: mergedArtifact };
  const predecessor = artifact.supersedesProposalId ? artifacts[artifact.supersedesProposalId] : undefined;
  if (artifact.supersedesProposalId && predecessor) {
    artifacts[artifact.supersedesProposalId] = {
      ...predecessor,
      status: "REPLACED",
      binding: undefined,
    };
  }
  return {
    state: {
      ...state,
      artifacts,
      families: { ...state.families, [artifact.familyId]: artifact.proposalId },
      stagedSelectionPatches: {
        ...state.stagedSelectionPatches,
        [artifact.proposalId]: state.stagedSelectionPatches[artifact.proposalId] ?? {
          includeItemKeys: [],
          excludeItemKeys: [],
        },
      },
      execution: {
        ...state.execution,
        [artifact.proposalId]: state.execution[artifact.proposalId] ?? {
          completed: artifact.receipt
            ? artifact.receipt.applied + artifact.receipt.stale + artifact.receipt.rejected + artifact.receipt.failed
            : 0,
          total: artifact.counts.selected,
          itemOutcomes: {},
        },
      },
    },
    applied: true,
    proposalId: artifact.proposalId,
  };
}

export function applyActionProgress(
  state: ActionArtifactState,
  event: { proposalId: string; version: number; lifecycleSequence: number; completed: number; total: number },
): ActionArtifactState {
  const artifact = state.artifacts[event.proposalId];
  if (!artifact || event.version !== artifact.version || event.lifecycleSequence <= artifact.lifecycleSequence) {
    return state;
  }
  return {
    ...state,
    artifacts: {
      ...state.artifacts,
      [event.proposalId]: { ...artifact, status: "APPLYING", lifecycleSequence: event.lifecycleSequence, resumable: false },
    },
    execution: {
      ...state.execution,
      [event.proposalId]: {
        ...(state.execution[event.proposalId] ?? { itemOutcomes: {} }),
        completed: event.completed,
        total: event.total,
      },
    },
  };
}

export function applyActionItemOutcome(
  state: ActionArtifactState,
  event: {
    proposalId: string;
    version: number;
    lifecycleSequence: number;
    itemKey: string;
    status: ActionProposalItemStatus;
    message: string;
    resultEntityId?: string;
  },
): ActionArtifactState {
  const artifact = state.artifacts[event.proposalId];
  if (!artifact || event.version !== artifact.version || event.lifecycleSequence < artifact.lifecycleSequence) {
    return state;
  }
  const execution = state.execution[event.proposalId] ?? { completed: 0, total: artifact.counts.selected, itemOutcomes: {} };
  return {
    ...state,
    execution: {
      ...state.execution,
      [event.proposalId]: {
        ...execution,
        itemOutcomes: {
          ...execution.itemOutcomes,
          [event.itemKey]: {
            status: event.status,
            message: event.message,
            ...(event.resultEntityId ? { resultEntityId: event.resultEntityId } : {}),
          },
        },
      },
    },
  };
}

export function applyActionLifecycle(
  state: ActionArtifactState,
  event: {
    proposalId: string;
    version: number;
    lifecycleSequence: number;
    status: ActionProposalStatus;
    counts?: ActionReceiptSummary;
    receipt?: ActionReceiptSummary;
    resumable?: boolean;
  },
): ActionArtifactState {
  const artifact = state.artifacts[event.proposalId];
  if (!artifact || event.version !== artifact.version || event.lifecycleSequence <= artifact.lifecycleSequence) {
    return state;
  }
  return {
    ...state,
    artifacts: {
      ...state.artifacts,
      [event.proposalId]: {
        ...artifact,
        status: event.status,
        lifecycleSequence: event.lifecycleSequence,
        resumable: event.resumable ?? false,
        ...(event.receipt ? { receipt: event.receipt } : {}),
      },
    },
  };
}

export function stageActionSelection(state: ActionArtifactState, proposalId: string, patch: ActionSelectionPatch): ActionArtifactState {
  return {
    ...state,
    stagedSelectionPatches: { ...state.stagedSelectionPatches, [proposalId]: patch },
  };
}
